import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, Heart, MessageCircle, Send, Bookmark, X, Expand } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { ChannelIcon } from "@/components/ChannelIcon";

// Live Instagram-style preview. Used by studio and client.
export function PostPreview({ post, brand, onVideoTime, rounded }) {
  const [idx, setIdx] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [zoom, setZoom] = useState(null);
  const videoRef = useRef(null);
  const files = post.files || [];
  const file = files[idx];
  const ratio = (post.channel === "instagram_reel" || post.channel === "instagram_historia") ? "aspect-[9/16]" : "aspect-[4/5]";

  useEffect(() => { setIdx(0); }, [post.id]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !onVideoTime) return;
    const handler = () => onVideoTime(v.currentTime, v);
    v.addEventListener("timeupdate", handler);
    return () => v.removeEventListener("timeupdate", handler);
  }, [file, onVideoTime]);

  const text = post.text || "";
  const isLong = text.length > 120;
  const shown = expanded || !isLong ? text : text.slice(0, 120);
  const accent = brand?.color || "#16161A";

  return (
    <div className={`w-full max-w-[400px] mx-auto bg-surface border border-line ${rounded ? "rounded-3xl overflow-hidden" : ""}`} data-testid="post-preview">
      {/* header */}
      <div className="flex items-center gap-3 px-3 py-2.5 border-b border-line">
        <div className="w-8 h-8 rounded-full overflow-hidden border border-line bg-app flex items-center justify-center shrink-0">
          {brand?.logo_url ? <img src={fileUrl(brand.logo_url)} alt="" className="w-full h-full object-cover" /> :
            <span className="font-display text-sm" style={{ color: accent }}>{brand?.name?.[0] || "·"}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{brand?.name || "Marca"}</div>
        </div>
        <ChannelIcon channel={post.channel} size={16} className="text-subtle" />
      </div>

      {/* media */}
      <div className={`relative bg-app ${ratio} flex items-center justify-center overflow-hidden`}>
        {file ? (
          file.type === "video" ? (
            <video ref={videoRef} src={fileUrl(file.url)} poster={post.cover_url ? fileUrl(post.cover_url) : undefined} controls preload="metadata" playsInline className="w-full h-full object-contain bg-black" data-testid="preview-video" />
          ) : (
            <>
              <img src={fileUrl(file.url)} alt="" onClick={() => setZoom(fileUrl(file.url))} className="w-full h-full object-cover cursor-zoom-in" data-testid="preview-image" />
              <span className="absolute bottom-2 right-2 bg-ink/60 text-white rounded-full p-1.5 opacity-0 hover:opacity-100 transition-opacity pointer-events-none"><Expand size={14} /></span>
            </>
          )
        ) : (
          <div className="text-subtle text-sm text-center px-6">Aún no hay imagen. Sube una a la derecha.</div>
        )}
        {files.length > 1 && (
          <>
            {idx > 0 && (
              <button onClick={() => setIdx(idx - 1)} data-testid="preview-prev" className="absolute left-2 top-1/2 -translate-y-1/2 bg-surface/90 border border-line w-7 h-7 flex items-center justify-center rounded-full">
                <ChevronLeft size={16} />
              </button>
            )}
            {idx < files.length - 1 && (
              <button onClick={() => setIdx(idx + 1)} data-testid="preview-next" className="absolute right-2 top-1/2 -translate-y-1/2 bg-surface/90 border border-line w-7 h-7 flex items-center justify-center rounded-full">
                <ChevronRight size={16} />
              </button>
            )}
            <div className="absolute top-2 right-2 font-data text-[11px] bg-ink/70 text-white px-1.5 py-0.5 rounded-sm">{idx + 1}/{files.length}</div>
          </>
        )}
      </div>

      {/* actions */}
      <div className="flex items-center gap-4 px-3 py-2.5">
        <Heart size={20} strokeWidth={1.5} />
        <MessageCircle size={20} strokeWidth={1.5} />
        <Send size={20} strokeWidth={1.5} />
        <Bookmark size={20} strokeWidth={1.5} className="ml-auto" />
      </div>

      {/* caption */}
      <div className="px-3 pb-4 text-sm leading-relaxed">
        <span className="font-medium mr-1.5">{brand?.name}</span>
        <span className="whitespace-pre-wrap">{shown}</span>
        {isLong && !expanded && (
          <button onClick={() => setExpanded(true)} className="text-subtle ml-1" data-testid="preview-see-more">… ver más</button>
        )}
        {post.hashtags && <div className="mt-1.5 text-[13px]" style={{ color: accent }}>{post.hashtags}</div>}
      </div>

      {zoom && (
        <div className="fixed inset-0 z-[60] bg-ink/90 flex items-center justify-center p-4 sm:p-8" onClick={() => setZoom(null)} data-testid="image-lightbox">
          <img src={zoom} alt="" className="max-w-full max-h-full object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          <button onClick={() => setZoom(null)} data-testid="close-lightbox" className="absolute top-4 right-4 text-white/80 hover:text-white bg-ink/40 rounded-full p-2"><X size={22} /></button>
        </div>
      )}
    </div>
  );
}
